import { useI18n } from "@baton/i18n";
import { Building2, Phone, Plus, SquareCheck } from "@baton/icons";
import { IconButton, Kbd, ShortcutMenu, type ShortcutMenuItem } from "@baton/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router";
import { accountAccessOf } from "../../accounts/permissions.ts";
import { meQuery } from "../../app/queries.ts";
import { useMenuOpensDialog } from "../../app/menuOpensDialog.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { useUiStore } from "../../app/uiStore.ts";
import { useMediaQuery } from "../layout/AppShell.tsx";
import { clientScopeOf } from "../modes/clientScope.ts";
import { sprintOfPath } from "../../cycle/data/sprintContext.ts";
import { globalShortcutKeys } from "../keyboard/keyboard.ts";

/**
 * `contracts/screens.md` § Top bar and § 020 Accounts and pipeline, the block of 2026-09-27, The
 * quick-create menu; `contracts/design-system.md` § Keyboard § Global (`n`): one small menu, Create,
 * opened by the top bar's plus and by `n` from anywhere outside a text input, with New task (`t`),
 * Log a call or meeting (`l`) and New client (`c`), the first focused so `n` then `Enter` makes a
 * task, and a bottom sheet below `--bp-md`. A row the reader may not use is not drawn: logging a call
 * needs `workspace.account.interaction.create` and a new client `workspace.account.create`, both the
 * `clients` area at write (020 FR-020-178, FR-020-244), so a reader who may use New task alone, a guest
 * among them, has the plus and `n` open the New task dialog straight away. On a screen of the Clients
 * area New task opens the dialog with its client context (the Client property and, on a screen
 * scoped to one client, that client set), and Log a call or meeting opens with the client set there.
 *
 * On the development side New task takes the screen's context too (2026-10-03, by the owner's
 * approval of the product review): on a sprint's page the task is made in that sprint, and on My
 * work it is assigned to the reader, so a task captured there lands where the reader is looking.
 */

export interface QuickCreate {
  /** The menu's rows this reader may use, in order. */
  readonly items: readonly ShortcutMenuItem[];
  /** Whether New task is the only row, so the plus and `n` open it without the menu. */
  readonly taskOnly: boolean;
  readonly newTask: () => void;
  readonly logCall: (() => void) | null;
  readonly newClient: (() => void) | null;
  /** What the plus and `n` do: the menu, or New task straight away. */
  readonly open: () => void;
}

export function useQuickCreate(ws: string): QuickCreate {
  const { t } = useI18n();
  const runtime = useRuntime();
  const me = useQuery(meQuery(runtime));
  const { pathname } = useLocation();
  const setNewItemOpen = useUiStore((state) => state.setNewItemOpen);
  const setLogInteraction = useUiStore((state) => state.setLogInteraction);
  const setNewClientOpen = useUiStore((state) => state.setNewClientOpen);
  const setQuickCreateOpen = useUiStore((state) => state.setQuickCreateOpen);
  const writesClients = accountAccessOf(me.data, ws).write;
  const scope = clientScopeOf(ws, pathname);
  const { clients, accountId } = scope;
  const sprintId = sprintOfPath(pathname, ws);
  const myWorkAssignee = pathname === `/${ws}/my-work` ? (me.data?.id ?? null) : null;
  const newTask = useCallback(() => {
    if (clients) setNewItemOpen(true, null, null, { accountId, milestoneStageId: null });
    // No project named: the dialog takes the route's project inside a project and No project elsewhere (`NewItemDialog.tsx`, 2026-09-28).
    else setNewItemOpen(true, null, sprintId, null, myWorkAssignee === null ? null : [myWorkAssignee]);
  }, [clients, accountId, sprintId, myWorkAssignee, setNewItemOpen]);
  const logCall = useMemo(
    () =>
      writesClients
        ? () => {
            setLogInteraction({ open: true, accountId });
          }
        : null,
    [writesClients, accountId, setLogInteraction],
  );
  const newClient = useMemo(
    () =>
      writesClients
        ? () => {
            setNewClientOpen(true);
          }
        : null,
    [writesClients, setNewClientOpen],
  );
  const items = useMemo<ShortcutMenuItem[]>(
    () => [
      { id: "task", label: t("shell.create.task"), icon: <SquareCheck />, letter: "t", onSelect: newTask },
      ...(logCall === null ? [] : [{ id: "call", label: t("shell.create.call"), icon: <Phone />, letter: "l", onSelect: logCall }]),
      ...(newClient === null ? [] : [{ id: "client", label: t("shell.create.client"), icon: <Building2 />, letter: "c", onSelect: newClient }]),
    ],
    [t, newTask, logCall, newClient],
  );
  const taskOnly = items.length === 1;
  const open = useCallback(() => {
    if (taskOnly) newTask();
    else setQuickCreateOpen(true);
  }, [taskOnly, newTask, setQuickCreateOpen]);
  return { items, taskOnly, newTask, logCall, newClient, open };
}

/** The top bar's plus: the quick-create menu, or New task alone for a reader who may use nothing else. */
export function QuickCreateButton({ ws }: { readonly ws: string }) {
  const { t } = useI18n();
  const quick = useQuickCreate(ws);
  const menuOpen = useUiStore((state) => state.quickCreateOpen);
  const setMenuOpen = useUiStore((state) => state.setQuickCreateOpen);
  const narrow = useMediaQuery("(width < 768px)");
  // Every row opens a dialog, which takes the focus itself.
  const { opens, onCloseAutoFocus } = useMenuOpensDialog();
  const items = useMemo(() => quick.items.map((item) => ({ ...item, onSelect: opens(item.onSelect) })), [quick.items, opens]);
  const label = t("shell.create.label");
  // The button's tooltip is its name with the key that opens it, read from the registry (§ Key hints, 2026-10-15); the key is its `aria-keyshortcuts` and the menu's foot too.
  const trigger = <IconButton size="sm" icon={<Plus />} label={label} shortcut={globalShortcutKeys("quick-create")} {...(quick.taskOnly ? { onClick: quick.newTask } : { "aria-haspopup": "menu" as const })} />;
  if (quick.taskOnly) return trigger;
  return (
    <ShortcutMenu
      trigger={trigger}
      heading={t("shell.create.heading")}
      items={items}
      foot={
        <>
          <span className="sr-only">{t("shell.create.footSpoken", { key: "N" })}</span>
          <span className="contents" aria-hidden="true">
            {t("shell.create.foot")}
            <Kbd keys="N" />
          </span>
        </>
      }
      open={menuOpen}
      onOpenChange={setMenuOpen}
      sheet={narrow}
      onCloseAutoFocus={onCloseAutoFocus}
    />
  );
}
